import type { QueryClient } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref } from 'vue';
import { getApi } from './api';

/** Shared with list index and operational map — keep list/detail/map requests aligned. */
export const ACCOUNT_SHIPMENT_V2_INCLUDE = 'devices,stops' as const;

/** Boundary snapshot per stop (gpx-device-api `shipment_stops.geojson`). */
export type V2StopBoundaryGeoJson = {
  type: 'Feature';
  geometry: { type: string; coordinates: unknown };
  properties?: Record<string, unknown>;
};

/** V2 GET `/api/v2/accounts/{account}/shipments/{id}` — pass `include=stops` for stop payloads from device-api. */
export type V2ShipmentStop = {
  id?: number;
  sequence: number;
  type: string;
  geofence_id?: number;
  name?: string | null;
  /** Present for every stop (circle: client; geofence: server-derived centroid). */
  center: { lat: number; lng: number };
  radius?: number | null;
  planned_departure_at?: string | null;
  planned_arrival_at?: string | null;
  geojson: V2StopBoundaryGeoJson;
};

export type V2ShipmentRoute = Record<string, unknown>;

/** Embedded when `?include=devices` (engine V2 show/index). */
export type V2ShipmentDevice = {
  id: number;
  name?: string | null;
  serial?: string | null;
  imei?: string | null;
};

export type V2ShipmentDetail = {
  id: number;
  uuid?: string;
  account_id: number;
  /** Creator (engine row); present on list/show when set. */
  user_id?: number | null;
  device_ids?: number[];
  devices?: V2ShipmentDevice[];
  name?: string;
  carrier?: string | null;
  notes?: string | null;
  asset_value?: number | null;
  created_at?: string | null;
  start_at?: string | null;
  started_at?: string | null;
  end_at?: string | null;
  ended_at?: string | null;
  archived_at?: string | null;
  /** Severity bitmask: info=1, warning=2, critical=4 */
  exception_flags?: number;
  notify_at?: number | null;
  status?: string;
  notification_emails?: string[];
  notification_phone_numbers?: string[];
  route_deviation_enabled?: boolean;
  stops?: V2ShipmentStop[];
  routes?: V2ShipmentRoute[];
};

export function accountShipmentV2QueryKey(
  accountId: number,
  shipmentId: number,
): readonly [
  'v2',
  'account-shipment',
  typeof ACCOUNT_SHIPMENT_V2_INCLUDE,
  number,
  number,
] {
  return [
    'v2',
    'account-shipment',
    ACCOUNT_SHIPMENT_V2_INCLUDE,
    accountId,
    shipmentId,
  ];
}

export async function loadAccountShipmentV2(
  accountId: number,
  shipmentId: number,
): Promise<V2ShipmentDetail> {
  const { api, baseURL } = getApi();
  const res = await api.get(
    `/api/v2/accounts/${accountId}/shipments/${shipmentId}`,
    { baseURL, params: { include: ACCOUNT_SHIPMENT_V2_INCLUDE } },
  );
  return (res.data?.data ?? res.data) as V2ShipmentDetail;
}

function useGetAccountShipmentV2(
  accountId: Ref<number | null | undefined> | number,
  shipmentId: Ref<number | null | undefined> | number,
) {
  return useQuery({
    queryKey: computed(() => {
      const aid = unref(accountId);
      const sid = unref(shipmentId);
      const valid =
        aid != null &&
        aid !== 0 &&
        sid != null &&
        typeof sid === 'number' &&
        sid > 0;
      if (!valid) {
        return [
          'v2',
          'account-shipment',
          ACCOUNT_SHIPMENT_V2_INCLUDE,
          'idle',
        ] as const;
      }
      return accountShipmentV2QueryKey(aid, sid);
    }),
    queryFn: async () => {
      const aid = unref(accountId);
      const sid = unref(shipmentId);
      if (aid == null || aid === 0 || sid == null || sid === 0) {
        throw new Error('Missing account or shipment id');
      }
      const { api, baseURL } = getApi();
      const res = await api.get(`/api/v2/accounts/${aid}/shipments/${sid}`, {
        baseURL,
        params: { include: ACCOUNT_SHIPMENT_V2_INCLUDE },
      });
      return (res.data?.data ?? res.data) as V2ShipmentDetail;
    },
    enabled: () => {
      const aid = unref(accountId);
      const sid = unref(shipmentId);
      return (
        aid != null &&
        aid !== 0 &&
        sid != null &&
        typeof sid === 'number' &&
        sid > 0
      );
    },
  });
}

async function ensureAccountShipmentV2QueryData(
  queryClient: QueryClient,
  accountId: number,
  shipmentId: number,
): Promise<V2ShipmentDetail> {
  return queryClient.ensureQueryData({
    queryKey: accountShipmentV2QueryKey(accountId, shipmentId),
    queryFn: () => loadAccountShipmentV2(accountId, shipmentId),
  });
}

const getAccountShipmentV2 = Object.assign(useGetAccountShipmentV2, {
  queryKey: accountShipmentV2QueryKey,
  ensureQueryData: ensureAccountShipmentV2QueryData,
});

export default getAccountShipmentV2;
